<script lang="ts">
  import { onDestroy } from "svelte";
  import { browser } from "$app/environment";
  import { goto } from "$app/navigation";
  import { ripple } from "svelte-ripple-action";
  import Portal from "../Common/Portal.svelte";
  import type { ButtonConfig } from "./types";
  import { MODAL_TYPES, MODAL_STATUS } from "./types";
  import { ContentSteeringController } from "hls.js";

  let { width, title = $bindable(""), content = $bindable(""), type = $bindable(MODAL_TYPES.CONFIRM), status = $bindable(MODAL_STATUS.NONE), confirmText = $bindable("确定"), cancelText = $bindable("取消"), showClose = $bindable(false), showBtn = $bindable(true), show = $bindable(false), children = null, onConfirm = () => {}, class: className = "", onCancel = () => {}, onClose = () => {}, login = $bindable(false), register = $bindable(false), appendToBody = false, append, contentAuto = false, confirmLoading = false, showConfirmButton = $bindable(true), showCancelButton = $bindable(true) } = $props();
  let visible = $state(false);
  let animating = $state(false);
  let hideTimer = null; // 改为普通变量，不使用$state

  // 根据 type 字段自动设置按钮显示状态
  const buttonConfig = $derived.by((): ButtonConfig => {
    switch (type) {
      case MODAL_TYPES.ALERT:
        return {
          showConfirm: true,
          showCancel: false,
        };
      case MODAL_TYPES.CONFIRM:
      case MODAL_TYPES.CANCEL:
      default:
        return {
          showConfirm: showConfirmButton,
          showCancel: showCancelButton,
        };
    }
  });

  let display = $state(!appendToBody);

  // 处理弹窗显示隐藏逻辑
  $effect(() => {
    if (!browser) return;

    if (show) {
      // 清除之前的隐藏定时器
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
      // 显示弹窗：立即设置visible为true
      visible = true;
      animating = false;
      // 禁用body滚动
      document.body.style.overflow = "hidden";
    } else {
      // 隐藏弹窗：开始动画
      animating = true;
      // 恢复body滚动
      document.body.style.overflow = "";

      // 清除之前的定时器
      if (hideTimer) {
        clearTimeout(hideTimer);
      }

      // 设置延迟隐藏，不在effect中修改状态
      hideTimer = setTimeout(() => {
        visible = false;
        animating = false;
      }, 310);
    }
  });

  const handleOverlayClick = (e: MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeModal(e);
      handleCancel(e);
    } else {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const closeModal = (e: Event, cb?: (e: Event) => void) => {
    // 直接设置show为false，让$effect处理动画逻辑
    show = false;

    // 在动画完成后执行回调
    setTimeout(() => {
      onClose?.(e);
      cb?.(e);
    }, 310);
  };

  const handleCancel = (e: Event) => {
    closeModal(e);
    onCancel?.(e);
  };
  const handleConfirm = (e: Event) => {
    console.log("close");
    closeModal(e);
    onConfirm?.(e);
  };

  const gotoLogin = (e: Event) => {
    closeModal(e, () => {
      goto("/auth/login");
    });
  };

  const gotoRegister = (e: Event) => {
    closeModal(e, () => {
      goto("/auth/reg");
    });
  };

  onDestroy(() => {
    if (!browser) return;
    if (hideTimer) {
      clearTimeout(hideTimer);
    }
    document.body.style.overflow = "";
  });
</script>

{#if visible}
  {#if appendToBody}
    <Portal
      onsuccess={() => {
        display = true;
      }}
    >
      {@render modalHtml()}
    </Portal>
  {:else}
    {@render modalHtml()}
  {/if}
{/if}

{#snippet modalHtml()}
  <div class="sv-modal fixed left-0 right-0 top-0 bottom-0 m-auto z-[1001] max-w-[430px] lg:!max-w-[430px] items-center justify-center overscroll-none {display ? 'flex' : 'hidden'}">
    <div class="absolute top-0 left-0 right-0 bottom-0 inset-0 bg-black/50 transition duration-600 opacity-0" onclick={handleOverlayClick} aria-hidden="true" style="-webkit-backdrop-filter: blur(2px); backdrop-filter: blur(2px); will-change: opacity;" class:opacity-100={show && !animating} class:opacity-0={!show || animating} class:duration-300={!show || animating}></div>
    <div class="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center animate__animated {show && !animating ? 'animate__zoomInDown' : 'animate__zoomOutDown'}">
      <div class="relative max-w-[90%] min-w-[60%] w-[78%] max-h-[80vh] bg-white rounded-[20px] flex flex-col" style="-webkit-transform: translateZ(0); will-change: transform, opacity; {width ? `width:${width}` : ''}">
        <!-- 图标 -->
        <div class="absolute left-1/2 -translate-x-1/2 -top-[25px]" style="-webkit-transform: translate(-50%, 0); will-change: transform;">
          {#if status === MODAL_STATUS.SUCCESS}
            {@render success()}
          {:else if status === MODAL_STATUS.ERROR}
            {@render error()}
          {:else if status === MODAL_STATUS.WARN}
            {@render warn()}
          {/if}
        </div>

        <div class="px-[17px] relative py-[23px] pt-[28px] flex flex-col min-h-0 flex-1 {className}">
          <!-- 关闭按钮 -->
          {#if showClose == true}
            <button class="absolute right-4 top-2.5 px-1 py-1.5" onclick={closeModal} aria-label="close">
              {@render close()}
            </button>
          {/if}
          <!-- 标题 -->
          {#if title}
            <div class="{status ? 'pt-[5px]' : ''} text-center flex-shrink-0">
              <h3 class="text-[20px] font-medium text-[var(--default)]">{title}</h3>
            </div>
          {/if}
          {#if children || content}
            <div class="{title || status ? 'mt-[30px]' : ''} {contentAuto ? `flex-1 overflow-y-auto custom-scrollbar` : ''}">
              <!-- 内容 -->
              {#if children}
                {@render children()}
              {:else}
                <div class="text-center text-[14px] text-[var(--default)] break-words">
                  {@html content}
                </div>
              {/if}
            </div>
          {/if}
          <!-- 按钮组 -->
          {#if showBtn}
            <div class="flex flex-col gap-2.5 flex-shrink-0 mt-[34px]">
              {#if buttonConfig.showConfirm}
                <button use:ripple class="btn btn-primary btn-block btn-lg h-[50px]" style="-webkit-tap-highlight-color: transparent; will-change: opacity;" onclick={handleConfirm}>
                  {#if confirmLoading}
                    <span class="loading loading-spinner loading-xs"></span>
                  {:else}
                    {confirmText}
                  {/if}
                </button>
              {/if}

              {#if buttonConfig.showCancel}
                <button use:ripple class="btn btn-outline btn-primary btn-block btn-lg" onclick={handleCancel}> {cancelText}</button>
              {/if}
            </div>
          {/if}
          {#if login}
            <button use:ripple class="text-[14px] text-[var(--default)] text-center mt-[15px]" onclick={gotoLogin}>已有账号，去<span class="text-[var(--primary)]">登录</span></button>
          {/if}
          {#if register}
            <button use:ripple class="text-[14px] text-[var(--default)] text-center mt-[15px]" onclick={gotoRegister}>暂无账号，去<span class="text-[var(--primary)]">注册</span></button>
          {/if}

          {@render append?.()}
        </div>
      </div>
    </div>
  </div>
{/snippet}

{#snippet success()}
  <svg xmlns="http://www.w3.org/2000/svg" width="51" height="51" viewBox="0 0 51 51" fill="none">
    <circle cx="25.5" cy="25.5" r="25.5" fill="#ffffff"></circle>
    <circle cx="25.5" cy="25.5" r="19.5" fill="var(--primary)"></circle>
    <path d="M17 25.5L24 32.5L35 17.5" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"></path>
  </svg>
{/snippet}

{#snippet error()}
  <svg xmlns="http://www.w3.org/2000/svg" width="51" height="51" viewBox="0 0 51 51" fill="none" class="text-[var(--error)]">
    <circle cx="25.5" cy="25.5" r="25.5" fill="#ffffff"></circle>
    <circle cx="25.5" cy="25.5" r="19.5" fill="currentColor"></circle>
    <path d="M17.3307 18.9274C17.119 18.7156 17 18.4285 17 18.129C17 17.8296 17.119 17.5424 17.3307 17.3307C17.5424 17.119 17.8296 17 18.129 17C18.4285 17 18.7156 17.119 18.9274 17.3307L34.6693 33.0726C34.7742 33.1775 34.8573 33.3019 34.9141 33.4389C34.9708 33.5759 35 33.7227 35 33.871C35 34.0192 34.9708 34.1661 34.9141 34.303C34.8573 34.44 34.7742 34.5645 34.6693 34.6693C34.5645 34.7742 34.44 34.8573 34.303 34.9141C34.1661 34.9708 34.0192 35 33.871 35C33.7227 35 33.5759 34.9708 33.4389 34.9141C33.3019 34.8573 33.1775 34.7742 33.0726 34.6693L17.3307 18.9274Z" fill="#ffffff"></path>
    <path d="M33.0726 17.3307C33.2844 17.119 33.5715 17 33.871 17C34.1704 17 34.4576 17.119 34.6693 17.3307C34.881 17.5424 35 17.8296 35 18.129C35 18.4285 34.881 18.7156 34.6693 18.9274L18.9274 34.6693C18.8225 34.7742 18.6981 34.8573 18.5611 34.9141C18.4241 34.9708 18.2773 35 18.129 35C17.9808 35 17.8339 34.9708 17.697 34.9141C17.56 34.8573 17.4355 34.7742 17.3307 34.6693C17.2258 34.5645 17.1427 34.44 17.0859 34.303C17.0292 34.1661 17 34.0192 17 33.871C17 33.7227 17.0292 33.5759 17.0859 33.4389C17.1427 33.3019 17.2258 33.1775 17.3307 33.0726L33.0726 17.3307Z" fill="#ffffff"></path>
  </svg>
{/snippet}

{#snippet warn()}
  <svg xmlns="http://www.w3.org/2000/svg" width="51" height="51" viewBox="0 0 51 51" fill="none" class="text-[var(--primary)]">
    <circle cx="25.5" cy="25.5" r="25.5" fill="#ffffff"></circle>
    <circle cx="25.5" cy="25.5" r="19.5" fill="var(--primary)"></circle>
    <path d="M23.9984 29.2488C24.25 29.9994 24.7492 30.4984 25.5 30.4984C26.2508 30.4984 26.75 29.9994 27.0016 29.2488L28 15.4992C28 13.9981 26.75 13 25.5 13C23.9984 13 23 14.2496 23 15.7507L23.9984 29.2488ZM25.5 33.0016C23.9984 33.0016 23 33.9997 23 35.5008C23 37.0019 23.9984 38 25.5 38C27.0016 38 28 37.0019 28 35.5008C28 33.9997 27.0016 33.0016 25.5 33.0016Z" fill="#ffffff"></path>
  </svg>
{/snippet}

{#snippet close()}
  <svg width="24" height="24" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16.8343 14.3175H16.8389V14.2898L16.8343 14.3175Z" fill="#999"></path>
    <path fill-rule="evenodd" clip-rule="evenodd" d="M9.00044 0C13.9626 0 18.0006 4.037 18.0006 9.00015C18.0006 13.963 13.9626 18.0003 9.00044 18.0003C4.037 18.0003 0 13.963 0 9.00015C0 4.037 4.037 0 9.00044 0ZM1.29168 9.00012C1.29168 13.2497 4.75022 16.7089 9.00044 16.7089C13.25 16.7089 16.7086 13.2497 16.7086 9.00012C16.7086 4.74993 13.25 1.29169 9.00044 1.29169C4.75022 1.29169 1.29168 4.74993 1.29168 9.00012Z" fill="#999"></path>
    <path d="M12.6878 11.8188L9.90491 9.02758L12.6853 6.27812C12.9377 6.02891 12.9396 5.62162 12.6904 5.3692C12.4412 5.11647 12.0332 5.11455 11.7808 5.36439L8.99787 8.11707L6.2564 5.36696C6.00529 5.11583 5.59859 5.11457 5.34684 5.36568C5.09571 5.61714 5.09571 6.0238 5.34556 6.27495L8.08384 9.02123L5.3239 11.7512C5.07148 12.001 5.06893 12.4071 5.3188 12.6595C5.445 12.787 5.60947 12.8507 5.77518 12.8507C5.93836 12.8507 6.10279 12.7883 6.22775 12.6653L8.99149 9.93146L11.777 12.7271C11.9025 12.8526 12.0676 12.9158 12.2327 12.9158C12.3972 12.9158 12.5609 12.8526 12.6865 12.7284C12.9377 12.4779 12.9383 12.0712 12.6878 11.8188Z" fill="#999"></path>
  </svg>
{/snippet}

<style>
  /* 自定义滚动条样式类 */
  .custom-scrollbar {
    scrollbar-width: 4px !important;
    scrollbar-color: #d1d5dc #f3f4f6 !important;
  }

  .custom-scrollbar::-webkit-scrollbar {
    display: block !important;
    width: 8px !important;
    height: 8px !important;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: #f3f4f6 !important;
    border-radius: 6px !important;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: #d1d5dc !important;
    border-radius: 6px !important;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #9ca3af !important;
  }

  .custom-scrollbar::-webkit-scrollbar-corner {
    background: #f3f4f6 !important;
  }
  @-webkit-keyframes modalIn {
    from {
      opacity: 0;
      -webkit-transform: translate3d(0, 20px, 0);
      transform: translate3d(0, 20px, 0);
    }
    to {
      opacity: 1;
      -webkit-transform: translate3d(0, 0, 0);
      transform: translate3d(0, 0, 0);
    }
  }

  @keyframes modalIn {
    from {
      opacity: 0;
      transform: translate3d(0, 20px, 0);
    }
    to {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }

  @-webkit-keyframes modalOut {
    from {
      opacity: 1;
      -webkit-transform: translate3d(0, 0, 0);
      transform: translate3d(0, 0, 0);
    }
    to {
      opacity: 0;
      -webkit-transform: translate3d(0, 20px, 0);
      transform: translate3d(0, 20px, 0);
    }
  }

  @keyframes modalOut {
    from {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
    to {
      opacity: 0;
      transform: translate3d(0, 20px, 0);
    }
  }
  .animate__zoomInDown {
    animation-duration: 0.6s;
  }
  .animate__zoomOutDown {
    animation-duration: 0.3s;
  }

  :global(.animate-modal) {
    -webkit-animation: modalIn 0.2s ease-out forwards;
    animation: modalIn 0.2s ease-out forwards;
    will-change: transform, opacity;
  }

  :global(.animate-modal-out) {
    -webkit-animation: modalOut 0.2s ease-out forwards;
    animation: modalOut 0.2s ease-out forwards;
    will-change: transform, opacity;
  }

  @media (prefers-reduced-motion: reduce) {
    :global(.animate-modal),
    :global(.animate-modal-out) {
      animation: none;
      -webkit-animation: none;
    }
  }
  [data-theme="blue"] {
    --primary: #4c9eea;
    --primary-grad: linear-gradient(270deg, #5fc2ff 0%, #2d96ff 100%);
    --default: #01325d;
  }
  [data-theme="orange"] {
    --primary: #ff7e07;
    --primary-grad: linear-gradient(270deg, #ffa44b 0%, #ff7e01 100%);
    --default: #2f0e00;
  }
  [data-theme="purple"] {
    --primary: #716ac7;
    --primary-grad: linear-gradient(270deg, #716ac7 0%, #47449b 100%);
    --default: #444370;
  }

  [data-theme="gold"] {
    --primary: #dca340;
    --primary-grad: linear-gradient(270deg, #ecc177 0%, #dca240 100%);
    --default: #573000;
  }

  .sv-modal .btn-primary {
    background: var(--primary-grad);
    color: #ffffff;
  }
  .sv-modal .btn-primary.btn-outline {
    background: #ffffff;
    color: var(--primary);
    border: 1px solid var(--primary) !important;
  }
</style>
